// NOTE (Noah, 2026-07-20): Shared detection + reload-loop guard for the
// stale-chunk failure that hits after a deploy when an already-open tab still
// references content-hashed lazy chunks that no longer exist
// (EDITOR-FRONTEND-7YS). Lives in replo-utils so the editor
// boundary, MiniAppShell, and MiniappErrorComponent share one signature.

const CHUNK_LOAD_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "is not a valid javascript mime type",
  "expected a javascript module script",
];

export const CHUNK_LOAD_RELOAD_TIMESTAMP_KEY = "replo.chunkLoadReloadAt";

// NOTE (Noah, 2026-07-20): If a reload doesn't resolve the chunk fetch within
// this window we assume reloading won't help (persistent 404, offline) and stop
// retrying, so we never trap the user in an infinite reload loop.
export const CHUNK_LOAD_RELOAD_LOOP_WINDOW_MS = 15_000;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  if (error instanceof Error && error.name === "ChunkLoadError") {
    return true;
  }
  const message = (
    error instanceof Error ? error.message : String(error)
  ).toLowerCase();
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

export function hasRecentlyReloadedForChunkLoad(): boolean {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_LOAD_RELOAD_TIMESTAMP_KEY);
    const last = raw ? Number(raw) : Number.NaN;
    return (
      Number.isFinite(last) &&
      Date.now() - last < CHUNK_LOAD_RELOAD_LOOP_WINDOW_MS
    );
  } catch {
    // NOTE (Noah, 2026-07-20): Without sessionStorage we can't detect loops
    // across reloads, so report "recently reloaded" to fall through to the
    // crash UI rather than risk reloading forever.
    return true;
  }
}

// NOTE (Noah, 2026-08-07): Vite's build-time preload helper emits
// "vite:preloadError" when a dynamic import (or one of its preloaded deps,
// e.g. a CSS chunk) fails to load — this fires before the rejection reaches
// any React error boundary, and also covers failures boundaries can't see.
// Reloading here is the recovery Vite's docs recommend; we share the same
// sessionStorage loop guard as the boundaries, and when a recent reload
// already failed we let the error throw so the crash dialog still surfaces.
export function installVitePreloadErrorReload() {
  window.addEventListener("vite:preloadError", (event) => {
    if (hasRecentlyReloadedForChunkLoad()) {
      return;
    }
    event.preventDefault();
    markChunkLoadReloaded();
    window.location.reload();
  });
}

export function markChunkLoadReloaded() {
  try {
    window.sessionStorage.setItem(
      CHUNK_LOAD_RELOAD_TIMESTAMP_KEY,
      String(Date.now()),
    );
  } catch {
    // Intentionally ignored: handled by hasRecentlyReloadedForChunkLoad's failure path.
  }
}
